import { Group, Paper, Text, UnstyledButton } from '@mantine/core';
import {
  IconChevronDown,
  IconChevronUp,
  IconPlayerPauseFilled,
  IconPlayerPlayFilled,
} from '@tabler/icons-react';
import { useId, useState } from 'react';
import { formatClock } from 'src/common/utils/formatClock';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { Waveform } from './Waveform';
import { waveformBars } from './waveformBars';
import classes from './AudioTranscriptCard.module.css';

type AudioTranscriptSegment = {
  id: number;
  speaker: string;
  text: string;
  startSeconds: number;
  endSeconds: number;
};

type AudioTranscriptCardProps = {
  segment: AudioTranscriptSegment;
  isPlaying: boolean;
  /** Playback position within this segment, 0..1. */
  progress: number;
  onTogglePlay: () => void;
  /** Start with the transcription revealed (e.g. a verify correction shows the differing value). */
  defaultExpanded?: boolean;
};

// Tuned for the narrow Siesta chat panel; bars flex to fill whatever width the card gets.
const BAR_COUNT = 56;

export function AudioTranscriptCard({
  segment,
  isPlaying,
  progress,
  onTogglePlay,
  defaultExpanded = false,
}: AudioTranscriptCardProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const transcriptId = useId();

  const clampedProgress = Math.min(1, Math.max(0, progress));
  const bars = waveformBars(segment.id, BAR_COUNT);
  const duration = Math.max(0, segment.endSeconds - segment.startSeconds);
  const clock = formatClock(isPlaying ? clampedProgress * duration : duration);

  const transcript = segment.text.trim();

  return (
    <Paper bg="contentBackground.0" p="md" withBorder style={{ borderRadius: 20 }}>
      <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.7" mb="xs">
        {segment.speaker}
      </Text>

      <Group gap="sm" align="center" wrap="nowrap">
        <UnstyledButton
          type="button"
          className={classes.play}
          onClick={onTogglePlay}
          aria-label={isPlaying ? 'Pause' : 'Play'}
        >
          {isPlaying ? (
            <IconPlayerPauseFilled size={16} aria-hidden />
          ) : (
            <IconPlayerPlayFilled size={18} aria-hidden />
          )}
        </UnstyledButton>
        <Waveform bars={bars} progress={clampedProgress} />
      </Group>

      <Group justify="space-between" align="center" mt="xs">
        <Text
          size="textXs"
          fw={FONT_WEIGHTS.semibold}
          c="gray.6"
          style={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {clock}
        </Text>
        <UnstyledButton
          type="button"
          className={classes.toggle}
          onClick={() => setExpanded(value => !value)}
          aria-expanded={expanded}
          aria-controls={transcriptId}
        >
          <Text size="textSm" fw={FONT_WEIGHTS.semibold} c="brand.7">
            {expanded ? 'Hide transcription' : 'Show transcription'}
          </Text>
          {expanded ? (
            <IconChevronUp size={14} aria-hidden />
          ) : (
            <IconChevronDown size={14} aria-hidden />
          )}
        </UnstyledButton>
      </Group>

      {expanded && transcript && (
        <Text id={transcriptId} size="textSm" c="gray.8" mt="sm" className={classes.quote}>
          &ldquo;{transcript}&rdquo;
        </Text>
      )}
    </Paper>
  );
}
